import DeleteIcon from '@mui/icons-material/Delete'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import GroupIcon from '@mui/icons-material/Group'
import PersonIcon from '@mui/icons-material/Person'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Typography
} from '@mui/material'
import React from 'react'

import db from '../../db/db'
import Database from '../../types/Database'
import { generateColorFromString } from '../../utils/FormatHelpers'

interface TeamBookingsListProps {
  teamBookings: Database.EnrichedTeamBooking[]
  onBookingDeleted: () => void
  currentUserId: string
}

export default function TeamBookingsList({
  teamBookings,
  onBookingDeleted,
  currentUserId
}: TeamBookingsListProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const [bookingToDelete, setBookingToDelete] =
    React.useState<Database.EnrichedTeamBooking | null>(null)
  const [isDeleting, setIsDeleting] = React.useState(false)
  const [deleteError, setDeleteError] = React.useState<string | null>(null)

  // Filter and sort future bookings
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const futureBookings = teamBookings
    .filter(booking => {
      if (!booking.forDate) return false

      // Handle Firestore Timestamp objects
      let bookingDate: Date
      if (
        booking.forDate &&
        typeof booking.forDate === 'object' &&
        'seconds' in booking.forDate
      ) {
        // Firestore Timestamp - convert seconds to milliseconds
        bookingDate = new Date((booking.forDate as any).seconds * 1000)
      } else {
        bookingDate = new Date(booking.forDate)
      }

      bookingDate.setHours(0, 0, 0, 0)
      return bookingDate >= today
    })
    .sort((a, b) => {
      if (!a.forDate || !b.forDate) return 0

      let dateA: number
      let dateB: number

      if (typeof a.forDate === 'object' && 'seconds' in a.forDate) {
        dateA = (a.forDate as any).seconds * 1000
      } else {
        dateA = new Date(a.forDate).getTime()
      }

      if (typeof b.forDate === 'object' && 'seconds' in b.forDate) {
        dateB = (b.forDate as any).seconds * 1000
      } else {
        dateB = new Date(b.forDate).getTime()
      }

      return dateA - dateB
    })

  const handleDeleteClick = (booking: Database.EnrichedTeamBooking) => {
    setBookingToDelete(booking)
    setDeleteDialogOpen(true)
  }

  const canDeleteBooking = (booking: Database.EnrichedTeamBooking): boolean => {
    // Allow deletion if current user is the booking owner OR the booking creator
    const isOwner = booking.bookingOwnerId === currentUserId
    const isCreator = booking.bookingCreatorId === currentUserId

    // For legacy bookings without bookingOwnerId, check uid
    const isLegacyOwner =
      !booking.bookingOwnerId && booking.uid === currentUserId

    return isOwner || isCreator || isLegacyOwner
  }

  const handleConfirmDelete = async () => {
    if (!bookingToDelete) return

    setIsDeleting(true)
    setDeleteError(null)

    try {
      if (!bookingToDelete.id) {
        throw new Error('Booking ID is missing')
      }
      await db.deleteItem('booking', bookingToDelete.id)
      setDeleteDialogOpen(false)
      setBookingToDelete(null)
      onBookingDeleted()
    } catch (error) {
      console.error('Error deleting booking:', error)
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to delete booking. Please check your connection and try again.'
      setDeleteError(errorMessage)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleRetryDelete = () => {
    setDeleteError(null)
    handleConfirmDelete()
  }

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false)
    setBookingToDelete(null)
  }

  const getOfficeColor = (officeName: string | undefined) => {
    return generateColorFromString(officeName || 'default')
  }

  const formatDate = (date: Date | undefined) => {
    if (!date) return 'Unknown date'

    // Handle Firestore Timestamp objects
    let jsDate: Date
    if (typeof date === 'object' && 'seconds' in date) {
      jsDate = new Date((date as any).seconds * 1000)
    } else {
      jsDate = new Date(date)
    }

    return jsDate.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <Box>
      {/* Team Bookings List */}
      {futureBookings.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <GroupIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant='h6' color='text.secondary' gutterBottom>
              No team bookings
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Your team members don't have any upcoming desk bookings
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <List sx={{ width: '100%' }}>
          {futureBookings.map((booking, index) => {
            const officeColor = getOfficeColor(booking.officeName || 'default')
            const isProxyBooking = booking.isProxyBooking && booking.creatorName
            return (
              <Card
                key={booking.id || index}
                sx={{
                  mb: 1.5,
                  '&:hover': {
                    boxShadow: 2
                  }
                }}
              >
                <ListItem
                  sx={{
                    py: 1.5,
                    px: 2
                  }}
                  secondaryAction={
                    canDeleteBooking(booking) ? (
                      <IconButton
                        edge='end'
                        aria-label='delete'
                        onClick={() => handleDeleteClick(booking)}
                        sx={{
                          color: 'error.main',
                          '&:hover': {
                            backgroundColor: 'error.light',
                            color: 'error.dark'
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    ) : null
                  }
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant='subtitle1'
                      component='div'
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      {formatDate(booking.forDate)}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mb: 0.5
                      }}
                    >
                      <PersonIcon
                        sx={{ fontSize: 16, color: 'text.secondary' }}
                      />
                      <Typography variant='body2' color='text.secondary'>
                        {booking.teamMemberName}
                      </Typography>
                    </Box>
                    {isProxyBooking && (
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ fontStyle: 'italic', display: 'block', mb: 0.5 }}
                      >
                        Booked by {booking.creatorName}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                      <Chip
                        label={booking.officeName || 'Unknown Office'}
                        size='small'
                        sx={{
                          backgroundColor: 'transparent',
                          border: `2px solid ${officeColor}`,
                          color: 'text.primary',
                          fontWeight: 600,
                          height: 24,
                          '&::before': {
                            content: '""',
                            display: 'inline-block',
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: officeColor,
                            marginRight: '6px'
                          }
                        }}
                      />
                      <Chip
                        label={booking.deskName || 'Unknown'}
                        size='small'
                        variant='outlined'
                        sx={{ height: 24 }}
                      />
                    </Box>
                  </Box>
                </ListItem>
              </Card>
            )
          })}
        </List>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby='delete-dialog-title'
        aria-describedby='delete-dialog-description'
      >
        <DialogTitle id='delete-dialog-title'>Delete Booking?</DialogTitle>
        <DialogContent>
          <DialogContentText id='delete-dialog-description'>
            Are you sure you want to delete this booking?
            {bookingToDelete && (
              <Box sx={{ mt: 2 }}>
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                  Date: {formatDate(bookingToDelete.forDate)}
                </Typography>
                <Typography variant='body2'>
                  Team Member: {bookingToDelete.teamMemberName}
                </Typography>
                <Typography variant='body2'>
                  Office: {bookingToDelete.officeName}
                </Typography>
                <Typography variant='body2'>
                  Desk: {bookingToDelete.deskName}
                </Typography>
              </Box>
            )}
          </DialogContentText>
          {deleteError && (
            <Alert severity='error' sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorOutlineIcon fontSize='small' />
                <Typography variant='body2'>{deleteError}</Typography>
              </Box>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={isDeleting}>
            Cancel
          </Button>
          {deleteError ? (
            <Button
              onClick={handleRetryDelete}
              color='primary'
              variant='contained'
              disabled={isDeleting}
            >
              {isDeleting ? <CircularProgress size={24} /> : 'Retry'}
            </Button>
          ) : (
            <Button
              onClick={handleConfirmDelete}
              color='error'
              variant='contained'
              disabled={isDeleting}
              autoFocus
            >
              {isDeleting ? <CircularProgress size={24} /> : 'Delete'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  )
}
